import { useCallback, useMemo } from "react";
import classnames from "classnames";
import moment from "moment";

const DEFAULT_CONDITION_VALUES = [
  'Good',
  'Dirty',
  'Damaged',
  'Not Working',
  'Missing',
  'Worn',
  'Unsafe'
];

const ChecklistSection = ({
  checklist,
  setChecklist,
  checklistTotals,
  checklistPct,
  isWorkOrderUpdateDisabled,
  workOrder,
  savingCompletion,
  lastSaved,
  openNotesId,
  toggleNotes,
  updateItemValue,
  clearItem,
  saveItemCompletion,
  setItemNotes,
  saveItemNotes,
}) => {
  // Helper to get item value display
  const getItemValue = useCallback((item) => {
    if (item.value === null || item.value === undefined) return null;
    
    switch (item.item_type) {
      case 'yes_no':
      case 'list':
        return item.value;
      case 'count':
        return item.number_value ?? item.value;
      case 'rating':
        return item.rating_value ?? item.value;
      case 'condition':
        return item.choice_value ?? item.value;
      default:
        return item.value;
    }
  }, []);

  if (!checklist || checklist.length === 0) {
    return null;
  }

  return (
    <div className="uk-width-1-1 uk-margin-top">
      <div className="uk-card uk-card-default uk-card-small" style={isWorkOrderUpdateDisabled ? { opacity: 0.6 } : {}}>
        <div className="uk-card-header uk-flex uk-flex-between uk-flex-middle">
          <div className="uk-flex uk-flex-middle" style={{ gap: 8 }}>
            <h4 className="uk-margin-remove">Checklist</h4>
            <span className="uk-badge">
              {checklistTotals.completed}/{checklistTotals.total} Completed
            </span>
            {isWorkOrderUpdateDisabled && (
              <span className="uk-badge uk-badge-warning">
                {workOrder?.status === 'new' ? 'Click Start to Begin' : 'Read Only'}
              </span>
            )}
            {savingCompletion && (
              <span className="uk-text-meta uk-text-small">
                <span uk-spinner="ratio: 0.6"></span> Saving...
              </span>
            )}
            {!savingCompletion && lastSaved && (
              <span className="uk-text-meta uk-text-small">
                Saved {moment(lastSaved).fromNow()}
              </span>
            )}
          </div>
          <div className="uk-flex uk-flex-middle" style={{ gap: 8 }}>
            <progress
              className="uk-progress"
              value={checklistPct}
              max="100"
              style={{ width: 120, height: 12, margin: 0 }}
            />
          </div>
        </div>

        <div className="uk-card-body">
          <ul className="uk-list uk-list-divider">
            {checklist.map((item) => {
              const itemType = item.item_type;
              const itemValue = getItemValue(item);
              
              return (
                <li key={item.id}>
                  <div className="uk-flex uk-flex-between uk-flex-middle uc-checklist-row">
                    <div className="uk-flex uk-flex-middle" style={{ gap: 8 }}>
                      <div>
                        <div className="uk-text-bold">
                          {item.label}
                          {item.is_required && (
                            <span className="uk-badge uc-badge-gray uk-margin-small-left">
                              Required
                            </span>
                          )}
                          {item.status === 'complete' && (
                            <span className="uk-badge uk-background-primary uk-margin-small-left">
                              ✓
                            </span>
                          )}
                        </div>
                        {item.description && (
                          <div className="uk-text-meta">{item.description}</div>
                        )}
                      </div>
                    </div>

                    <div className="uk-flex uk-flex-middle" uk-tooltip="Add a Note" style={{ gap: 8 }}>
                      <button
                        type="button"
                        className="uk-button uk-button-link uc-icon-btn"
                        onClick={() => toggleNotes(item.id)}
                        disabled={isWorkOrderUpdateDisabled}
                        style={item.require_note ? { color: '#f05252' } : {}}
                      >
                        <span data-uk-icon="icon: file-text"></span>
                      </button>
                    </div>
                  </div>

                  {/* Value Input Based on Type */}
                  <div className="uk-margin-small-top">
                    {itemType === 'yes_no' && (
                      <div className="uk-flex uk-flex-middle" style={{ gap: 8 }}>
                        <button
                          type="button"
                          className={classnames('task-type-btn', {
                            'is-on': itemValue === true
                          })}
                          onClick={() => updateItemValue(item.id, true, 'yes_no')}
                          disabled={isWorkOrderUpdateDisabled}
                        >
                          Yes
                        </button>

                        <button
                          type="button"
                          className={classnames('task-type-btn', {
                            'is-on': itemValue === false
                          })}
                          onClick={() => updateItemValue(item.id, false, 'yes_no')}
                          disabled={isWorkOrderUpdateDisabled}
                        >
                          No
                        </button>

                        <button
                          type="button"
                          className="uc-clear-pill"
                          onClick={() => clearItem(item.id)}
                          disabled={isWorkOrderUpdateDisabled}
                        >
                          <span data-uk-icon="icon: close"></span>
                          Clear
                        </button>
                      </div>
                    )}

                    {itemType === 'count' && (
                      <div className="uk-grid-small uk-child-width-1-2@s" data-uk-grid>
                        <div>
                          <input
                            className="uk-input"
                            type="number"
                            min={item.config?.min ?? item.min ?? 0}
                            max={item.config?.max ?? item.max ?? undefined}
                            value={itemValue ?? ''}
                            onChange={(e) => {
                              const val = e.target.value === '' ? null : parseInt(e.target.value);
                              setChecklist(prev => prev.map(i => i.id === item.id ? { ...i, value: val } : i));
                            }}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            placeholder="Enter count"
                            disabled={isWorkOrderUpdateDisabled}
                          />

                          <div className="uk-text-meta uk-margin-xsmall-top">
                            Min: {item.config?.min ?? item.min ?? 0}
                            {(item.config?.max ?? item.max) != null
                              ? ` • Max: ${item.config?.max ?? item.max}`
                              : ''}
                          </div>

                          <div className="uk-flex uk-flex-middle uk-margin-small-top" style={{ gap: 8 }}>
                            <button
                              type="button"
                              className="task-type-btn"
                              onClick={() => saveItemCompletion(item.id, 'value', itemValue)}
                              disabled={isWorkOrderUpdateDisabled}
                            >
                              Save
                            </button>
                            
                            <button
                              type="button"
                              className="uc-clear-pill"
                              onClick={() => clearItem(item.id)}
                              disabled={isWorkOrderUpdateDisabled}
                            >
                              <span data-uk-icon="icon: close"></span>
                              Clear
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {itemType === 'rating' && (
                      <div className="uk-flex uk-flex-middle" style={{ gap: 6, flexWrap: 'wrap' }}>
                        {Array.from(
                          {
                            length:
                              (item.config?.scale_max ?? item.scale_max ?? 5) -
                              (item.config?.scale_min ?? item.scale_min ?? 1) +
                              1
                          },
                          (_, idx) => (item.config?.scale_min ?? item.scale_min ?? 1) + idx
                        ).map((n) => (
                          <button
                            key={n}
                            type="button"
                            className={classnames('task-type-btn', {
                              'is-on': itemValue === n
                            })}
                            onClick={() => updateItemValue(item.id, n, 'rating')}
                            disabled={isWorkOrderUpdateDisabled}
                          >
                            {n}
                          </button>
                        ))}
                        <button
                          type="button"
                          className="uc-clear-pill"
                          onClick={() => clearItem(item.id)}
                          disabled={isWorkOrderUpdateDisabled}
                        >
                          <span data-uk-icon="icon: close"></span>
                          Clear
                        </button>
                      </div>
                    )}

                    {itemType === 'condition' && (
                      <div className="uk-flex uk-flex-middle" style={{ gap: 8, flexWrap: 'wrap' }}>
                        {DEFAULT_CONDITION_VALUES.map((c) => (
                          <button
                            key={c}
                            type="button"
                            className={classnames('task-type-btn', {
                              'is-on': itemValue === c
                            })}
                            onClick={() => updateItemValue(item.id, c, 'condition')}
                            disabled={isWorkOrderUpdateDisabled}
                          >
                            {c}
                          </button>
                        ))}
                        <button
                          type="button"
                          className="uc-clear-pill"
                          onClick={() => clearItem(item.id)}
                          disabled={isWorkOrderUpdateDisabled}
                        >
                          <span data-uk-icon="icon: close"></span>
                          Clear
                        </button>
                      </div>
                    )}

                    {itemType === 'list' && (
                      <div className="uk-margin-small-top">
                        <ul className="uk-list">
                          {(item.config?.items || []).map((listItem, idx) => (
                            <li key={idx}>
                              <span className="uk-margin-small-right" uk-icon="icon: check; ratio: 1"></span>
                              <span>{listItem}</span>
                            </li>
                          ))}
                        </ul>

                        <div className="uk-flex uk-flex-middle uk-margin-small-top" style={{ gap: 8 }}>
                          <button
                            type="button"
                            className={classnames('task-type-btn', {
                              'is-on': itemValue === true
                            })}
                            onClick={() => updateItemValue(item.id, true, 'list')}
                            disabled={isWorkOrderUpdateDisabled}
                          >
                            Done
                          </button>

                          <button
                            type="button"
                            className="uc-clear-pill"
                            onClick={() => clearItem(item.id)}
                            disabled={isWorkOrderUpdateDisabled}
                          >
                            <span data-uk-icon="icon: close"></span>
                            Clear
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {openNotesId === item.id && (
                    <div className="uk-margin-small-top uc-checklist-notes">
                      <div className="uk-text-meta uk-margin-xsmall-bottom">Notes</div>
                      <textarea
                        className="uk-textarea uk-form-small"
                        rows={2}
                        value={item.notes || ''}
                        placeholder="Add notes"
                        onChange={(e) => setItemNotes(item.id, e.target.value)}
                        disabled={isWorkOrderUpdateDisabled}
                      />
                      <div className="uk-flex uk-flex-middle uk-flex-right uk-margin-small-top" style={{ gap: 8 }}>
                        <button
                          type="button"
                          className="task-type-btn"
                          onClick={() => saveItemNotes(item.id, item.notes)}
                          disabled={isWorkOrderUpdateDisabled}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ChecklistSection;
